import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Typography,
} from '@mui/material';
import { Box } from '@mui/system';
import { addValidity } from 'api/validity/validity.api';
import { ValidityStatusEnum } from 'common/enums';
import {
  CustomAlert,
  DynamicDialog,
  ErrorDialog,
} from 'components/shared';
import { AuthStatus, useAuth } from 'contexts/auth/auth_context';
import { Field, Form, Formik } from 'formik';
import { IUser } from 'modules/auth/interfaces/storage.interface';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from 'routes/constants';
import { authService } from 'services/auth/auth_service';
import * as Yup from 'yup';
import RegisteredIcon from '../../assets/icons/registered-icon.svg';
import { Loading } from '../../components/login';
import TextFieldDataCustom from 'components/shared/textfiel_data_custom';

interface DialogState {
  isOpen: boolean;
  icon: string;
  title: string;
  message: string;
  buttonText: string;
  onLogout: (() => void) | null;
}

type DialogButtonConfig = {
  text: string;
  action: () => void;
  color:
  | 'inherit'
  | 'primary'
  | 'secondary'
  | 'success'
  | 'error'
  | 'info'
  | 'warning';
};

type ErrorDialogState = {
  open: boolean;
  title: string;
  message1: string;
  message2?: string | null;
  button1: DialogButtonConfig;
  button2?: DialogButtonConfig | null;
};

// exported helpers (top-level so Jest can import them)
export const validateManagementTerm = (values: any) => {
  const errors: any = {};
  let dateError = '';
  if (values?.managementTerm?.endDate && values?.managementTerm?.startDate) {
    if (new Date(values.managementTerm.endDate) < new Date(values.managementTerm.startDate)) {
      errors.managementTerm = { endDate: 'A data final deve ser posterior à data inicial.' };
      dateError = 'A data final deve ser posterior à data inicial.';
    }
  }
  return { errors, dateError };
};

export const mapAuthStatus = (status: AuthStatus) => {
  switch (status) {
    case AuthStatus.sessionExpired:
      return { type: 'navigate', route: ROUTES.TERMS_OF_USE_AND_CONFIDENTIALITY };
    case AuthStatus.validityExpired:
      return { type: 'validityExpired' };
    case AuthStatus.userBlocked:
      return { type: 'userBlocked' };
    case AuthStatus.userInactive:
      return { type: 'userInactive' };
    case AuthStatus.userNotFound:
      return { type: 'userNotFound' };
    case AuthStatus.requestError:
      return { type: 'requestError' };
    default:
      return { type: 'none' };
  }
};

export const isSuccessStatusNavigateHome = (status: AuthStatus) => {
  return [AuthStatus.userAcceptTerms, AuthStatus.userAuthorized].includes(status);
};

export const hasPendingValidity = (userInfo: any) => {
  return userInfo?.validityList?.some((validity: any) => {
    if (validity.status === ValidityStatusEnum.INACTIVE) {
      if (validity.endAt) {
        const currentDate = new Date();
        const endDate = new Date(validity.endAt);
        return currentDate < endDate;
      }
      return true;
    }
    return false;
  });
};

export const buildValidityPayload = (values: any, userUuid?: string) => {
  return {
    userUuid: userUuid,
    dataInicial: new Date(values.managementTerm.startDate),
    dataFinal: values.managementTerm.endDate !== '' ? new Date(values.managementTerm.endDate) : null,
    vigenciaIndeterminada: !!values.managementTerm.endDateIndefinite,
  };
};

export const getUserValidityExpiredDialogState = (
  logoutFn: () => void,
  openValidityFn: () => void,
  clearErrorDialogFn: () => void,
): ErrorDialogState => ({
  open: true,
  title: 'Termo de vigência expirado',
  message1:
    'Representante legal da instuição cadastrada está com sua vigência expirada, por favor cadastrar nova vigência',
  button1: {
    text: 'Fechar',
    action() {
      logoutFn();
    },
    color: 'primary',
  },
  button2: {
    text: 'Cadastrar Vigência',
    action() {
      openValidityFn();
      clearErrorDialogFn();
    },
    color: 'primary',
  },
});

export const getSessionExpiredDialogState = (logoutFn: () => void): ErrorDialogState => ({
  open: true,
  title: 'Sua sessão expirou',
  message1:
    'Por segurança a sessão expirou. Você deve voltar para a tela inicial e entrar novamente.',
  button1: {
    text: 'FECHAR',
    action() {
      logoutFn();
    },
    color: 'primary',
  },
});

export const getUserBlockedDialogState = (logoutFn: () => void): ErrorDialogState => ({
  open: true,
  title: 'Usuário Bloqueado',
  message1: 'O acesso do Usuário informado encontra-se bloqueado.',
  message2: `Orientamos que entre em contato com a Administração do sistema para realizar o desbloqueio do Usuário, ou Informe outro
        Usuário para realizar o acesso na página anterior. Entre em contato com <a href="mailto:seiaf@agriculturafamiliar.mt.gov.br">seiaf@agriculturafamiliar.mt.gov.br</a>.`,
  button1: {
    text: 'voltar',
    action() {
      logoutFn();
    },
    color: 'primary',
  },
});

export const getUserInactiveDialogState = (logoutFn: () => void): ErrorDialogState => ({
  open: true,
  title: 'Usuário Inativo',
  message1: 'O acesso do Usuário informado não está ativo.',
  message2: `Orientamos que entre em contato com a Administração do sistema para verificar o status de acesso deste Usuário,
        ou Informe outro Usuário para realizar o acesso na página anterior. Para mais informações: (65) 98142-0114 ou seiaf@agriculturafamiliar.mt.gov.br`,
  button1: {
    text: 'voltar',
    action() {
      logoutFn();
    },
    color: 'primary',
  },
});

export const getUserNotFoundDialogState = (logoutFn: () => void): ErrorDialogState => ({
  open: true,
  title: 'Usuário Não Encontrado',
  message1: 'O acesso do Usuário informado não foi localizado.',
  message2: `Orientamos que entre em contato com a Administração do sistema para verificar o status de acesso deste Usuário,
        ou Informe outro Usuário para realizar o acesso na página anterior.`,
  button1: {
    text: 'voltar',
    action() {
      logoutFn();
    },
    color: 'primary',
  },
});

export const getRequestErrorDialogState = (logoutFn: () => void, refreshFn: () => void): ErrorDialogState => ({
  open: true,
  title: 'Ocorreu um erro na sua solicitação',
  message1: 'Deseja atualizar a página atual?',
  button1: {
    text: 'fechar',
    action() {
      logoutFn();
    },
    color: 'inherit',
  },
  button2: {
    text: 'atualizar',
    action() {
      refreshFn();
    },
    color: 'primary',
  },
});

const LoginValidation: React.FC = () => {
  const navigate = useNavigate();
  const { authenticateWithMtLogin, logout } = useAuth();

  const [errorDialogState, setErrorDialogState] = useState<ErrorDialogState>({
    open: false,
    title: '',
    message1: '',
    button1: {
      text: '',
      action: () => { },
      color: 'inherit',
    },
    button2: null,
  });

  const [dialogState, setDialogState] = useState<DialogState>({
    isOpen: false,
    icon: '',
    title: '',
    message: '',
    buttonText: 'Concluir',
    onLogout: null,
  });

  const initialValues = {
    managementTerm: {
      startDate: '',
      endDate: '',
      endDateIndefinite: false,
    },
  };

  const [user, setUser] = useState<IUser>();
  const [validityModalVisibility, setValidityModalVisibility] = useState(false);
  const [dateError, setDateError] = useState('');
  const handleSubmit = async (values: typeof initialValues) => {
    await addValidity(buildValidityPayload(values, user?.uuid));

    setValidityModalVisibility(false);
    setDialogState({
      isOpen: true,
      icon: RegisteredIcon,
      title:
        'Vigência cadastrada com sucesso - Será realizado o seu logout do sistema',
      message: `Identificamos que você é Representante Legal e está realizando o cadastro da vigência no seu próprio cadastro,
          desta forma realizaremos o seu logout do sistema.O cadastro permanecerá inativado até que o gerente estadual valide as informações da nova vigência.`,
      buttonText: 'Concluir',
      onLogout: () => logout(),
    });
  };

  const validationSchema = Yup.object({
    managementTerm: Yup.object({
      startDate: Yup.date().required('Informe a data inicial corretamente.'),
      endDateIndefinite: Yup.boolean().test(
        'is-indefinite-disabled',
        'Não é permitido definir data final indefinida em modo de edição.',
        function (value) {
          return true;
        },
      ),
      endDate: Yup.date().nullable(),
    }),
  });



  const handleRefresh = () => {
    window.location.reload();
  };

  const handleSetSessionExpiredDialog = useCallback(() => {
    setErrorDialogState(getSessionExpiredDialogState(() => logout()));
  }, [logout]);

  const handleSetUserBlockedDialog = useCallback(() => {
    setErrorDialogState(getUserBlockedDialogState(() => logout()));
  }, [logout]);
  const handleSetUserValidityExpiredDialog = useCallback(() => {
    setErrorDialogState(
      getUserValidityExpiredDialogState(
        () => logout(),
        () => setValidityModalVisibility(true),
        () =>
          setErrorDialogState({
            open: false,
            title: '',
            message1: '',
            button1: {
              text: '',
              action: () => { },
              color: 'inherit',
            },
            button2: null,
          }),
      ),
    );
  }, [logout]);

  const handleSetUserInactiveDialog = useCallback(() => {
    setErrorDialogState(getUserInactiveDialogState(() => logout()));
  }, [logout]);

  const handleSetUserNotFoundDialog = useCallback(() => {
    setErrorDialogState(getUserNotFoundDialogState(() => logout()));
  }, [logout]);

  const handleSetRequestErrorDialog = useCallback(() => {
    setErrorDialogState(getRequestErrorDialogState(() => logout(), () => handleRefresh()));
  }, [logout]);

  useEffect(() => {
    const handleAuthentication = async (codeFromUrl: string) => {
      const result = await authenticateWithMtLogin(codeFromUrl);
      handleAuthenticationResult(result);
    };

    const handleAuthenticationResult = (result: any) => {
      if (!result.success) {
        handleErrorStatus(result.status, result.userInfo);
        return;
      }

      handleSuccessStatus(result.status);
    };

    const handleErrorStatus = (status: AuthStatus, userInfo: any) => {
      const mapped = mapAuthStatus(status);
      switch (mapped.type) {
        case 'navigate':
          navigate(mapped.route as string);
          break;
        case 'validityExpired':
          setUser(userInfo);
          handleValidityCheck(userInfo);
          break;
        case 'userBlocked':
          handleSetUserBlockedDialog();
          break;
        case 'userInactive':
          handleSetUserInactiveDialog();
          break;
        case 'userNotFound':
          handleSetUserNotFoundDialog();
          break;
        case 'requestError':
          handleSetRequestErrorDialog();
          break;
        default:
          break;
      }
    };

    const handleSuccessStatus = (status: AuthStatus) => {
      if (
        [AuthStatus.userAcceptTerms, AuthStatus.userAuthorized].includes(status)
      ) {
        navigate(ROUTES.HOME);
      }
    };

    const handleValidityCheck = (userInfo: any) => {
      const pending = hasPendingValidity(userInfo);

      if (!pending) {
        handleSetUserValidityExpiredDialog();
        return;
      }

      setDialogState({
        isOpen: true,
        icon: RegisteredIcon,
        title: 'Vigência aguardando aprovação',
        message: `Identificamos que você é Representante Legal e que tem uma vigência com análise pendente. Realizaremos o seu logout até que o gerente estadual valide as informações da nova vigência.`,
        buttonText: 'Concluir',
        onLogout: () => logout(),
      });
    };

    const validateLogin = async () => {
      const codeFromUrl = authService.getCodeFromUrl();

      if (!codeFromUrl) {
        handleSetSessionExpiredDialog();
        return;
      }

      await handleAuthentication(codeFromUrl);
    };

    validateLogin();
  }, [
    authenticateWithMtLogin,
    handleSetRequestErrorDialog,
    handleSetSessionExpiredDialog,
    handleSetUserBlockedDialog,
    handleSetUserInactiveDialog,
    handleSetUserNotFoundDialog,
    navigate,
  ]);

  return (
    <>
      <Loading />
      <DynamicDialog
        isOpen={dialogState.isOpen}
        icon={dialogState.icon}
        title={dialogState.title}
        message={dialogState.message}
        buttonText='Concluir'
        onClose={() => {
          setDialogState((prevState) => ({
            ...prevState,
            isOpen: false,
            onLogout: null,
          }));
        }}
        onLogout={dialogState.onLogout || undefined}
      />
      <Dialog open={validityModalVisibility} fullWidth maxWidth='lg'>
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography
            sx={{
              fontFamily: 'Roboto',
              fontSize: '24px',
              fontWeight: 500,
              lineHeight: '38.4px',
              letterSpacing: '0.15px',
              textAlign: 'left',
              color: '#000000DE',
              marginTop: '20px',
            }}
          >
            Adicionar Nova Vigência de Gestão
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
            validate={(values) => {
              const errors: any = {};
              if (
                values.managementTerm.endDate &&
                values.managementTerm.startDate
              ) {
                if (
                  new Date(values.managementTerm.endDate) <
                  new Date(values.managementTerm.startDate)
                ) {
                  errors.managementTerm = {
                    endDate: 'A data final deve ser posterior à data inicial.',
                  };
                  setDateError(
                    'A data final deve ser posterior à data inicial.',
                  );
                } else {
                  setDateError('');
                }
              }
              return errors;
            }}
            validateOnMount={true}
            validateOnChange={true}
          >
            {({
              handleChange,
              values,
              handleBlur,
              touched,
              errors,
              isValid,
              setFieldValue,
            }) => (
              <Form>
                {dateError && (
                  <CustomAlert severity='error' message={dateError} />
                )}
                <Box
                  sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}
                >
                  <Box
                    sx={{ display: 'flex', flexDirection: 'row', gap: '16px' }}
                  ></Box>

                  <Box
                    sx={{ display: 'flex', flexDirection: 'row', gap: '16px' }}
                  >
                    <Box sx={{ flexBasis: '50%' }}>
                      <TextFieldDataCustom
                        label='Data inicial'
                        type='date'
                        size='small'
                        fullWidth
                        onChange={(e) => {
                          handleChange(e);
                          setFieldValue('managementTerm.startDate', e.target.value);
                        }}
                        error={
                          touched.managementTerm?.startDate &&
                          Boolean(errors.managementTerm?.startDate)
                        }
                        InputLabelProps={{ shrink: true }}
                        value={values.managementTerm.startDate}
                      />
                    </Box>

                    <Box sx={{ flexBasis: '50%', display: 'grid', gap: '8px' }}>
                      <TextFieldDataCustom
                        label='Data Final'
                        type='date'
                        size='small'
                        fullWidth
                        onChange={(e) => {
                          handleChange(e);
                          setFieldValue('managementTerm.endDate', e.target.value);
                        }}
                        error={
                          touched.managementTerm?.endDate &&
                          Boolean(errors.managementTerm?.endDate)
                        }
                        InputLabelProps={{ shrink: true }}
                        disabled={values.managementTerm.endDateIndefinite}
                        value={values.managementTerm.endDateIndefinite ? '' : values.managementTerm.endDate}
                      />
                      <FormControlLabel
                        control={
                          <Field
                            name='managementTerm.endDateIndefinite'
                            type='checkbox'
                            as={Checkbox}
                            onChange={(
                              e: React.ChangeEvent<HTMLInputElement>,
                            ) => {
                              handleChange(e);
                              if (e.target.checked) {
                                setFieldValue('managementTerm.endDate', '');
                                setDateError('');
                              }
                            }}
                          />
                        }
                        label='Data Final Indeterminada'
                        componentsProps={{
                          typography: {
                            sx: {
                              fontFamily: 'Roboto',
                              fontSize: '16px',
                              fontWeight: 400,
                              lineHeight: '24px',
                              letterSpacing: '0.15px',
                              textAlign: 'left',
                            },
                          },
                        }}
                      />
                    </Box>
                  </Box>
                </Box>
                <DialogActions
                  sx={{
                    justifyContent: 'flex-start',
                    padding: 0,
                    margin: '0px 0px 30px 0px',
                  }}
                >
                  <Button
                    variant='contained'
                    size='medium'
                    sx={{ backgroundColor: '#0048B6' }}
                    type='submit'
                    disabled={!isValid}
                  >
                    Confirmar
                  </Button>
                  <Button size='medium' onClick={() => logout()}>
                    Fechar
                  </Button>
                </DialogActions>
              </Form>
            )}
          </Formik>
        </DialogContent>
      </Dialog>

      <ErrorDialog
        open={errorDialogState.open}
        title={errorDialogState.title}
        message1={errorDialogState.message1}
        {...(errorDialogState.message2 && {
          message2: errorDialogState.message2,
        })}
        button1Text={errorDialogState.button1.text}
        button1Action={errorDialogState.button1.action}
        button1Color={errorDialogState.button1.color}
        {...(errorDialogState.button2 && {
          button2Text: errorDialogState.button2.text,
          button2Action: errorDialogState.button2.action,
          button2Color: errorDialogState.button2.color,
        })}
      />
    </>
  );
};

export default LoginValidation;
